import { useState } from "react";
import { Can } from "@core/permissions";
import { ApiError } from "@core/http";
import { markErrorHandled, reportError } from "@portal/common/errorMessage";
import { Icon } from "@core/icons";
import type { Profile } from "@core/types";
import { tokens } from "@core/theme/tokens";
import {
  Badge,
  FeedbackState,
  ImpactConfirmDialog,
  RowIconAction,
  SecurityHeader,
  useToast,
} from "@portal/common";
import { CursorTable, type CursorTableColumn } from "@core/table";
import { ProfileForm } from "./ProfileForm";
import type { ProfileDraft } from "./ProfileForm";
import { useProfileMutations, useProfiles } from "./profileQueries";
import { profileStyles } from "./profileStyles";

// SPEC-216 §8: textos exactos de la tabla de mensajes. `PROFILE_HAS_USERS`
// no está acá porque no es un rechazo (`ErrorCode`): es una confirmación con
// impacto (`ConfirmationCode`, 409) que se resuelve con ImpactConfirmDialog,
// no con este mensaje de error genérico.
const ERROR_MESSAGES: Record<string, string> = {
  PROFILE_DUPLICATE: "Ya existe un perfil con ese nombre",
  SYSTEM_PROFILE: "Los perfiles iniciales no se inactivan ni se eliminan",
  ADMIN_SECURITY: "El perfil Administrador conserva siempre acceso a Seguridad",
  VALIDATOR_ONLY_PROFILE:
    "Un perfil de comercio aliado no puede tener permisos del portal",
};
function usersAffectedOf(error: ApiError): number {
  const impact = error.problem?.impact as
    { usersAffected?: number } | undefined;
  return impact?.usersAffected ?? 0;
}

export function ProfilesScreen() {
  const toast = useToast();
  const profiles = useProfiles();
  const mutations = useProfileMutations();
  const [editing, setEditing] = useState<Profile | null | undefined>(undefined);
  const [inactivateTarget, setInactivateTarget] = useState<{
    profile: Profile;
    usersAffected: number;
  } | null>(null);
  // Mismo criterio que `CatalogsScreen`/`MembersScreen`: `PROFILE_DUPLICATE`
  // (RN, `authorization.rules.ts`) ahora identifica el campo (`name`) — se lo
  // pasamos a `ProfileForm` para que `applyApiFieldErrors` lo asocie; si no
  // puede, cae al toast de siempre (`onUnhandledSubmitError`).
  const [submitError, setSubmitError] = useState<unknown>(undefined);
  const handleUnhandledSubmitError = (error: unknown) =>
    reportError(toast, error, ERROR_MESSAGES);
  const permissionDenied =
    profiles.error instanceof ApiError && profiles.error.status === 403;
  const columns: CursorTableColumn<Profile>[] = [
    {
      key: "profile",
      header: "Perfil",
      // 260px y no los 200px de antes: el nombre convive con hasta dos
      // insignias ("Perfil del sistema" + "Solo validador", el caso de
      // "Comercio"), que a 200px quedaban recortadas por el `overflow:hidden`
      // de la celda. `flexWrap` es la red de seguridad para nombres largos:
      // la segunda insignia baja de línea en vez de cortarse.
      width: "260px",
      render: (profile) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            flexWrap: "wrap",
            rowGap: 6,
          }}
        >
          <span
            style={{
              font: `600 13px ${tokens.font.ui}`,
              color: tokens.text.primary,
            }}
          >
            {profile.name}
          </span>
          {profile.isSystem ? (
            <Badge tone="neutral">Perfil del sistema</Badge>
          ) : null}
          {profile.validatorOnly ? (
            <Badge tone="achievement">Solo validador</Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: "description",
      header: "Descripción",
      // Fluida: absorbe lo que se llevó la columna "Perfil" sin empujar la
      // tabla a un scroll horizontal nuevo, con un piso legible.
      width: "minmax(240px, 1fr)",
      render: (profile) => (
        <span
          style={{
            font: `400 12px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
          }}
        >
          {profile.description ?? "—"}
        </span>
      ),
    },
    {
      key: "users",
      header: "Usuarios asignados",
      width: "185px",
      render: (profile) => (
        <span
          style={{
            font: `500 12.5px ${tokens.font.ui}`,
            color: tokens.text.secondary,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {profile.users} usuario{profile.users === 1 ? "" : "s"}
        </span>
      ),
    },
    {
      key: "state",
      header: "Estado",
      width: "120px",
      render: (profile) => (
        <Badge tone={profile.active ? "silver" : "neutral"}>
          {profile.active ? "Activo" : "Inactivo"}
        </Badge>
      ),
    },
  ];

  const save = (input: ProfileDraft) => {
    if (!editing) {
      mutations.create.mutate(input, {
        onSuccess: () => {
          setEditing(undefined);
          toast.show("Perfil creado correctamente.");
        },
        onError: (error) => setSubmitError(error),
      });
      return;
    }
    mutations.edit.mutate(
      { id: editing.id, input },
      {
        onSuccess: () => {
          setEditing(undefined);
          toast.show("Cambios guardados correctamente.");
        },
        onError: (error) => setSubmitError(error),
      },
    );
  };

  // Primera llamada sin `confirmed`. Si el perfil tiene usuarios asignados,
  // el backend responde 409 PROFILE_HAS_USERS con el conteo (RF-SEG-15): se
  // abre el diálogo de impacto en vez de inactivar de una — es un aviso, no
  // un error de servicio, así que se queda en el diálogo, no en el toast.
  const requestInactivation = (profile: Profile) => {
    mutations.inactivate.mutate(
      { id: profile.id, confirmed: false },
      {
        onError: (error) => {
          if (error instanceof ApiError && error.code === "PROFILE_HAS_USERS") {
            setInactivateTarget({
              profile,
              usersAffected: usersAffectedOf(error),
            });
            markErrorHandled(error);
            return;
          }
          reportError(toast, error, ERROR_MESSAGES);
        },
      },
    );
  };
  const confirmInactivation = () => {
    if (!inactivateTarget) return;
    mutations.inactivate.mutate(
      { id: inactivateTarget.profile.id, confirmed: true },
      {
        onSuccess: () => {
          setInactivateTarget(null);
          toast.show("Perfil inactivado correctamente.");
        },
        onError: (error) => reportError(toast, error, ERROR_MESSAGES),
      },
    );
  };

  return (
    <div>
      <SecurityHeader />

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 16,
        }}
      >
        <Can module="security" action="canCreate">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setEditing(null)}
          >
            <Icon name="plus" size={15} /> Nuevo perfil
          </button>
        </Can>
      </div>

      {profiles.isLoading ? (
        <FeedbackState kind="loading" />
      ) : permissionDenied ? (
        <FeedbackState kind="permission-denied" />
      ) : profiles.isError ? (
        <FeedbackState kind="error" />
      ) : profiles.data?.length ? (
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 940 }}>
            <CursorTable
              columns={columns}
              rows={profiles.data}
              rowKey={(profile) => profile.id}
              page={{ totalCount: profiles.data.length, nextCursor: null }}
              onLoadMore={() => undefined}
              actionsHeader="Acciones"
              actionWidth="190px"
              rowActions={(profile) => (
                <>
                  <Can module="security" action="canEdit">
                    <RowIconAction
                      label="Editar"
                      icon="edit"
                      onClick={() => setEditing(profile)}
                    />
                  </Can>
                  {!profile.isSystem ? (
                    <Can module="security" action="canInactivate">
                      <RowIconAction
                        label="Inactivar"
                        icon="deactivate"
                        tone="danger"
                        onClick={() => requestInactivation(profile)}
                      />
                    </Can>
                  ) : null}
                </>
              )}
            />
          </div>
        </div>
      ) : (
        <FeedbackState kind="empty" />
      )}

      {editing !== undefined ? (
        <ProfileForm
          profile={editing ?? undefined}
          modal={{
            label: editing ? "Editar perfil" : "Nuevo perfil",
            width: profileStyles.modal.width,
            onClose: () => setEditing(undefined),
          }}
          onSubmit={save}
          onCancel={() => setEditing(undefined)}
          submitting={mutations.create.isPending || mutations.edit.isPending}
          submitError={submitError}
          onUnhandledSubmitError={handleUnhandledSubmitError}
        />
      ) : null}

      <ImpactConfirmDialog
        open={Boolean(inactivateTarget)}
        eyebrow="Seguridad · Perfiles"
        title={`¿Inactivar el perfil «${inactivateTarget?.profile.name ?? ""}»?`}
        affectedCount={inactivateTarget?.usersAffected ?? 0}
        affectedLabel="usuarios quedarían sin acceso hasta que se les asigne otro perfil."
        onCancel={() => setInactivateTarget(null)}
        onConfirm={confirmInactivation}
        confirmLabel="Sí, inactivar"
      />
    </div>
  );
}
